import { useEffect, useRef, useState } from "react";

import { Button } from "../ui/Button";
import { getAccessToken } from "../../lib/api";
import { type ExportFormat, type ReportName, reportsApi } from "../../lib/api/reports";
import { t } from "../../lib/i18n";

interface ExportMenuProps {
  reportName: ReportName;
  filenameStem: string;
  filters: Record<string, unknown>;
}

const FORMATS: ReadonlyArray<{ format: ExportFormat; labelKey: string }> = [
  { format: "csv", labelKey: "reports.export.csv" },
  { format: "xlsx", labelKey: "reports.export.xlsx" },
];

export function ExportMenu({ reportName, filenameStem, filters }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const download = async (format: ExportFormat) => {
    setBusy(format);
    try {
      const url = reportsApi.exportUrl(reportName, format, filters);
      const token = getAccessToken();
      const res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        console.error("Report export failed", res.status, await res.text());
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `${filenameStem}.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <Button variant="secondary" onClick={() => setOpen((v) => !v)} disabled={busy !== null}>
        {t("reports.export.label")} ▾
      </Button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 160,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-strong)",
            borderRadius: 6,
            boxShadow: "var(--shadow-lg)",
            padding: 4,
            zIndex: 40,
          }}
        >
          {FORMATS.map(({ format, labelKey }) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              onClick={() => void download(format)}
              disabled={busy !== null}
              style={{
                display: "block",
                width: "100%",
                padding: "8px 12px",
                border: 0,
                borderRadius: 4,
                background: "transparent",
                textAlign: "left",
                cursor: busy ? "wait" : "pointer",
                font: "inherit",
                fontSize: 13,
              }}
            >
              {t(labelKey)}
              {busy === format && " …"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
